import React, { useState, useEffect, useRef } from "react";
import "./Dashboard.scss";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { isDateInPast } from "../../utils/isDateInPast";
import { usePostHog } from "posthog-js/react";
import clsx from "classnames";
import { useTranslation } from "react-i18next";
import { gameList } from "../../gameList";
import { Button } from "@/components/ui/button";
import { PURCHASES_CLOSED, SHUTDOWN_DATE_TEXT } from "../../shutdown";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import {
  Clock,
  PcCase,
  Gamepad,
  Power,
  Check,
  Copy,
  ChevronsLeftRightEllipsis,
  Loader,
  CopyCheck,
  X,
  AppWindowMac,
} from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import AddPlaytimeDialog from "../../components/AddPlaytimeDialog/AddPlaytimeDialog";
import DownloadAppDialog from "../../components/DownloadAppDialog/DownloadAppDialog";
import HowToStart from "../../components/HowToStart/HowToStart";

const gamingRigStatusLink =
  "https://jly4ch5l57.execute-api.ap-south-1.amazonaws.com/default/gamingRigStatus";

const gamingRigStatusOptions = {
  client_loading: "client_loading",
  not_running: "not_running",
  running: "running",
  booting: "booting",
};

const isNewGameFlagSet = () => {
  const queryString = window.location.search;
  const urlParams = new URLSearchParams(queryString);
  const allowNewGame = urlParams.get("allowNewGame");
  return !!(allowNewGame && allowNewGame === "true");
};

const defaultSelectedGame = "Grand Theft Auto V Steam";

const Dashboard = ({
  user,
  userPlan,
  setUserPlan,
  paymentGateway,
  determineCountry,
}) => {
  const posthog = usePostHog();
  const allowNewGameFlag = useRef(isNewGameFlagSet()).current;
  const gameFromParams = new URLSearchParams(useLocation().search).get("game");
  const [state, setState] = useState({
    gamingRigStatus: gamingRigStatusOptions.client_loading,
    gamingRigIp: false,
    hoursRemaining: "Unknown",
    pin: "",
    pinUrl: "",
    pinSubmitted: false,
    areInstructionsExpanded: true,
    showCopiedTooltip: false,
    showPinLoader: false,
    showPinTick: false,
    gameName: gameFromParams || defaultSelectedGame,
    newGameName: "",
    newGameStore: "steam",
    gameSize: 0,
    loadingRigIsStartingStopping: false,
    cancellingBoot: false,
    hasShownRigStartNotification: false,
    allowNewGame: !!allowNewGameFlag,
    suggestedGame: "",
    isGameSuggested: false,
    showGameSuggestion: false,
    paymentPlanExpiryDate: "",
    startGameRequestError: false,
    allowTwoHoursForFifty: false,
    isHourlyPlan: true,
    accessAllGames: false,
    progress: 0,
    isYourOwnGamePlan: false,
    vm: false,
    vmDate: null,
    closestRegion: null,
    hasHours: false,
    startGamePleaseWait: false,
    region: "ap-south-1",
    launchingApp: false,
    expiringHoursRemaining: [],
  });
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [purchaseHours, setPurchaseHours] = useState(20);
  const [checkoutEmail, setCheckoutEmail] = useState(null);
  const [sharedVms, setSharedVms] = useState([]);
  const [howToOpen, setHowToOpen] = React.useState(false);
  const [launchingApp, setLaunchingApp] = React.useState(false);
  const [privatePcSelected, setPrivatePcSelected] = React.useState(
    gameFromParams ? false : true
  );
  const [downloadingApp, setDownloadingApp] = React.useState(false);
  const [downloadUrl, setDownloadUrl] = React.useState(null);
  const [isDeterminingUrl, setIsDeterminingUrl] = useState(true);

  const intervalRef = useRef(null);
  const { t } = useTranslation();
  const navigate = useNavigate();

  const RAZORPAY_SCRIPT_URL = "https://checkout.razorpay.com/v1/checkout.js";
  const RAZORPAY_KEY = "rzp_live_u8YAfYrwkuCTLq";

  const isIOS = () => {
    return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  };

  const loadScript = (src) => {
    return new Promise((resolve) => {
      const script = document.createElement("script");
      script.src = src;
      script.onload = () => {
        resolve(true);
      };
      script.onerror = () => {
        resolve(false);
      };
      document.body.appendChild(script);
    });
  };

  async function handleHoursBuy(quantity, payment_gateway, isHourlyPlan) {
    if (PURCHASES_CLOSED) {
      navigate("/shutdown");
      return;
    }
    setCheckoutLoading(true);
    await fetch(
      payment_gateway === "stripe"
        ? process.env.NODE_ENV === "development"
          ? "https://1ku3nifl2e.execute-api.ap-south-1.amazonaws.com/default/getStripeCheckout-dev-hello"
          : "https://v1wapno6ch.execute-api.ap-south-1.amazonaws.com/prod/getStripeCheckout-prod-hello"
        : process.env.NODE_ENV === "development"
          ? "https://9mljq6r4e3.execute-api.ap-south-1.amazonaws.com/default/getRazorpayCheckout-dev-hello"
          : "https://d58cycm35h.execute-api.ap-south-1.amazonaws.com/default/getRazorpayCheckout-prod-hello",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json;charset=UTF-8",
          Authorization: user.signInUserSession.idToken.jwtToken,
        },
        body: JSON.stringify({
          payment_mode: "payment",
          products: [
            {
              priceId: null,
              isHourlyPlan: isHourlyPlan,
              adjustableQuantity: false,
              quantity: quantity,
              email: checkoutEmail,
              vmMachineType: null,
              vmPaymentPeriod: null,
              vmPaymentType: "payment",
              hoursPaymentPeriod: null,
              hoursPaymentType: "payment",
            },
          ],
        }),
      }
    )
      .then((response) => response.json())
      .then((data) => {
        if (data.checkout_url) {
          window.location.href = data.checkout_url;
        } else if (data.order_id && payment_gateway === "razorpay") {
          const options = {
            key: data.key_id,
            amount: data.amount, // Amount should be in currency subunits
            currency: "INR",
            name: "GameAway",
            order_id: data.order_id,
            handler: function (response) {
              navigate("/");
            },
            prefill: {
              email: checkoutEmail,
            },
            theme: {
              color: "#231955",
            },
          };
          const paymentObject = new window.Razorpay(options);
          paymentObject.open();
        } else if (data.subscription_id && payment_gateway === "razorpay") {
          const options = {
            key: data.key_id, // Enter the Key ID generated from the Razorpay Dashboard
            subscription_id: data.subscription_id,
            name: "GameAway",
            handler: function (response) {
              navigate("/");
            },
            prefill: {
              email: checkoutEmail,
            },
            theme: {
              color: "#231955",
            },
          };
          const paymentObject = new window.Razorpay(options);
          paymentObject.open();
        }
        setCheckoutLoading(false);
      })
      .catch((error) => {
        console.error("Error:", error);
        setCheckoutLoading(false);
      });
  }

  useEffect(() => {
    loadScript(RAZORPAY_SCRIPT_URL).then((loaded) => {
      if (!loaded) {
        console.log(
          "Failed to load Razorpay SDK. Please check your internet connection and try again."
        );
      }
    });
    const getDownloadUrl = () => {
      setIsDeterminingUrl(true); // Indicate that URL determination is in progress
      if (
        navigator.userAgentData &&
        navigator.userAgentData.getHighEntropyValues
      ) {
        navigator.userAgentData
          .getHighEntropyValues(["platform"])
          .then((result) => {
            const platform = result.platform.toLowerCase();
            if (platform.includes("mac")) {
              setDownloadUrl(
                "https://github.com/utkarshdalal/GameAway-moonlight/releases/download/v1.0.1/GameAway-1.0.1.dmg"
              );
            } else if (platform.includes("win")) {
              setDownloadUrl(
                "https://github.com/utkarshdalal/GameAway-moonlight/releases/download/v1.0.1/GameAwaySetup-1.0.1.exe"
              );
            }
          })
          .catch(() => {
            console.error("Cannot determine platform using userAgentData");
          })
          .finally(() => {
            setIsDeterminingUrl(false); // Reset the URL determination indicator
          });
      } else {
        const platform = navigator.userAgent.toLowerCase();
        if (platform.includes("mac")) {
          setDownloadUrl(
            "https://github.com/utkarshdalal/GameAway-moonlight/releases/download/second-release/GameAway-1.0.0.dmg"
          );
        } else if (platform.includes("windows") || platform.includes("win")) {
          setDownloadUrl(
            "https://github.com/utkarshdalal/GameAway-moonlight/releases/download/second-release/GameAwaySetup-1.0.0.exe"
          );
        } else {
          console.log("Unable to determine platform from userAgent");
          // Optionally set a default or show an error/notification to the user
        }
      }
    };
    const getSharedVms = async () => {
      const sharedVmJson = await fetch(
        "https://kxro7kkpf9.execute-api.ap-south-1.amazonaws.com/default/getVmSharedUsers",
        {
          headers: {
            "Content-Type": "application/json;charset=UTF-8",
            Authorization: user.signInUserSession.idToken.jwtToken,
          },
        }
      );
      const sharedVms = await sharedVmJson.json();
      setSharedVms(sharedVms);
    };
    const requestNotificationPermission = async () => {
      if (Notification && Notification.requestPermission) {
        try {
          await Notification.requestPermission();
        } catch (err) {
          console.log("notification error", err);
        }
      }
    };
    const determineRegion = async () => {
      try {
        // Fetch the IP address
        const ipResponse = await fetch("https://api.ipify.org?format=json");
        const ipData = await ipResponse.json();
        const ip = ipData.ip;

        // Fetch location data using the IP
        const locationResponse = await fetch(`https://ipinfo.io/${ip}/json`);
        const locationData = await locationResponse.json();

        const locations = {
          Mumbai: { lat: 19.076, lon: 72.8777 },
          Bahrain: { lat: 26.0667, lon: 50.5577 },
          "Hong Kong": { lat: 22.3193, lon: 114.1694 },
          Milan: { lat: 45.4642, lon: 9.19 },
          "São Paulo": { lat: -23.5505, lon: -46.6333 },
          "Northern Virginia": { lat: 39.0437, lon: -77.4875 },
          Oregon: { lat: 43.8041, lon: -120.5542 },
        };

        const userLat = parseFloat(locationData.loc.split(",")[0]);
        const userLon = parseFloat(locationData.loc.split(",")[1]);

        let minDistance = Number.MAX_VALUE;
        let closestCity = "";
        Object.keys(locations).forEach((city) => {
          const distance = Math.sqrt(
            Math.pow(locations[city].lat - userLat, 2) +
            Math.pow(locations[city].lon - userLon, 2)
          );
          if (distance < minDistance) {
            minDistance = distance;
            closestCity = city;
          }
        });

        let closestRegion = "ap-south-1";
        if (closestCity === "Mumbai") {
          closestRegion = "ap-south-1";
        } else if (closestCity === "Bahrain") {
          closestRegion = "me-south-1";
        } else if (closestCity === "Hong Kong") {
          closestRegion = "ap-east-1";
        } else if (closestCity === "Milan") {
          closestRegion = "eu-south-1";
        } else if (closestCity === "São Paulo") {
          closestRegion = "sa-east-1";
        } else if (closestCity === "Northern Virginia") {
          closestRegion = "us-east-1";
        } else if (closestCity === "Oregon") {
          closestRegion = "us-west-2";
        }
        console.log("Closest region is: ", closestRegion);

        const lastSelectedServer = localStorage.getItem("serverSelection");
        if (!lastSelectedServer) {
          setState((prevState) => ({ ...prevState, region: closestRegion }));
        } else if (lastSelectedServer === closestRegion) {
          console.log(
            "Last selected state is same as closest region: ",
            closestRegion
          );
          setState((prevState) => ({ ...prevState, region: closestRegion }));
        } else {
          console.log("Region set to last selected: ", lastSelectedServer);
          setState((prevState) => ({
            ...prevState,
            region: lastSelectedServer,
          }));
        }
      } catch (error) {
        console.error("Error fetching IP or location:", error);
      }
    };
    requestNotificationPermission();
    checkInstanceState();
    determineRegion();
    getSharedVms();
    determineCountry();
    getDownloadUrl();

    const lastSelectedGame = localStorage.getItem("gameSelection");

    if (lastSelectedGame) {
      setState((prevState) => ({
        ...prevState,
        gameName: lastSelectedGame,
      }));
    }

    return () => {
      if (
        state.gamingRigStatus !== gamingRigStatusOptions.booting &&
        state.gamingRigStatus !== gamingRigStatusOptions.client_loading
      ) {
        clearInterval(intervalRef.current);
        localStorage.removeItem("loaderStartTime");
      }
    };
  }, []);

  useEffect(() => {
    if (state.gamingRigStatus === gamingRigStatusOptions.booting) {
      if (!localStorage.getItem("loaderStartTime")) {
        localStorage.setItem("loaderStartTime", Date.now());
      }
      intervalRef.current = setInterval(() => {
        const elapsedTime =
          Date.now() - Number(localStorage.getItem("loaderStartTime"));
        let minutesNeededToBoot = 2;
        switch (state.gameName) {
          case "Red Dead Redemption 2":
            minutesNeededToBoot = 4;
            break;
          case "Grand Theft Auto V Epic":
            minutesNeededToBoot = 3;
            break;
          default:
            minutesNeededToBoot = 2;
        }
        const progress = Math.min(
          (elapsedTime / (minutesNeededToBoot * 60 * 1000)) * 100,
          100
        );
        setState((prevState) => ({ ...prevState, progress }));
        if (progress >= 100) {
          clearInterval(intervalRef.current);
          localStorage.removeItem("loaderStartTime");
        }
      }, 1000);
    } else if (
      state.gamingRigStatus !== gamingRigStatusOptions.client_loading
    ) {
      clearInterval(intervalRef.current);
      setState((prevState) => ({ ...prevState, progress: 0 }));
      localStorage.removeItem("loaderStartTime");
    }

    return () => clearInterval(intervalRef.current);
  }, [state.gamingRigStatus]);

  const startProgress = () => {
    const startTime = localStorage.getItem("loaderStartTime");
    intervalRef.current = setInterval(() => {
      const elapsedTime = Date.now() - Number(startTime);
      const progress = Math.min((elapsedTime / (4 * 60 * 1000)) * 100, 100);
      setState((prevState) => ({ ...prevState, progress }));
      if (progress >= 100) {
        clearInterval(intervalRef.current);
        localStorage.removeItem("loaderStartTime");
      }
    }, 1000);
  };

  const showNotification = (text) => {
    try {
      if (Notification && Notification.permission === "granted") {
        new Notification(text);
        setState((prevState) => ({
          ...prevState,
          hasShownRigStartNotification: true,
        }));
      }
    } catch (err) {
      console.log(err);
    }
  };

  const handleIpCopy = (clipboardText) => {
    navigator.clipboard.writeText(clipboardText);
    setState((prevState) => ({ ...prevState, showCopiedTooltip: true }));
    setTimeout(() => {
      setState((prevState) => ({ ...prevState, showCopiedTooltip: false }));
    }, 1500);
  };

  const handlePinChange = (value) => {
    setState((prevState) => ({ ...prevState, pin: value }));
  };

  const startGamingRig = async (gameName) => {
    setState((prevState) => ({
      ...prevState,
      gamingRigStatus: gamingRigStatusOptions.booting,
      loadingRigIsStartingStopping: true,
    }));

    let gameNameToSend =
      gameName || (allowNewGameFlag ? state.newGameName : state.gameName);
    let sharingVmUser = null;

    if (gameNameToSend === state.gameName && gameNameToSend.includes("@")) {
      sharingVmUser = gameNameToSend;
      gameNameToSend = "VM";
    }

    try {
      window.dataLayer.push({
        event: "start_gaming_rig",
        selectedGame: gameNameToSend,
      });
    } catch (err) {
      console.log(err, "Error in GTM code");
    }

    try {
      const startGameResponse = await fetch(
        "https://yx539fwm53.execute-api.ap-south-1.amazonaws.com/default/startGamingRig",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json;charset=UTF-8",
            Authorization: user.signInUserSession.idToken.jwtToken,
          },
          body: JSON.stringify({
            game: gameNameToSend,
            sharing_vm_user: sharingVmUser,
            game_size: state.gameSize,
            email: checkoutEmail,
            region: gameNameToSend === "VM" ? state.region : "ap-south-1",
            ...(allowNewGameFlag && { store: state.newGameStore }),
          }),
        }
      );

      if (startGameResponse.status === 401) {
        setState((prevState) => ({
          ...prevState,
          startGameRequestError: true,
        }));
      }

      if (startGameResponse.status === 404) {
        setState((prevState) => ({
          ...prevState,
          startGamePleaseWait: true,
        }));
        setTimeout(() => {
          setState((prevState) => ({
            ...prevState,
            startGamePleaseWait: false,
          }));
        }, 15000);
      }

      if (startGameResponse.status === 504) {
        setTimeout(checkInstanceState, 15000);
      }

      await checkInstanceState();
      setState((prevState) => ({
        ...prevState,
        loadingRigIsStartingStopping: false,
      }));

      posthog?.capture("start_game", {
        selectedGame: state.gameName,
        userEmail: user.email,
      });
    } catch (error) {
      await checkInstanceState();
      setTimeout(checkInstanceState, 10000);
      setState((prevState) => ({
        ...prevState,
        loadingRigIsStartingStopping: false,
      }));
    }
  };

  const shutdownGamingRig = async () => {
    setState((prevState) => ({
      ...prevState,
      loadingRigIsStartingStopping: true,
      cancellingBoot: true,
    }));
    await fetch(
      "https://b9f178yc79.execute-api.ap-south-1.amazonaws.com/default/shutdownGamingRig",
      {
        method: "POST",
        mode: "cors",
        cache: "no-cache",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json;charset=UTF-8",
          Authorization: user.signInUserSession.idToken.jwtToken,
        },
        body: JSON.stringify({
          region: state.region,
        }),
      }
    );
    await checkInstanceState();
    setState((prevState) => ({
      ...prevState,
      loadingRigIsStartingStopping: false,
      cancellingBoot: false,
    }));
    posthog?.capture("stop_game", {
      selectedGame: state.gameName,
      userEmail: user.email,
    });
  };

  const setPin = async () => {
    setState((prevState) => ({
      ...prevState,
      pinSubmitted: true,
      showPinLoader: true,
    }));
    try {
      await fetch(`${state.pinUrl}${state.pin}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json;charset=UTF-8",
          Authorization: user.signInUserSession.idToken.jwtToken,
        },
      });
      setState((prevState) => ({
        ...prevState,
        pinSubmitted: false,
        showPinLoader: false,
        showPinTick: true,
      }));
    } catch (err) {
      console.log(err);
    }
    posthog?.capture("set_pin", {
      selectedGame: state.gameName,
      userEmail: user.email,
    });
  };

  const setPaymentPlanState = (gamingRigStatusJson) => {
    const {
      max_bitrate,
      hours_remaining,
      isHourlyPlan,
      vm,
      vmExpiry,
      expiring_hours_remaining,
    } = gamingRigStatusJson;
    const vmDate = new Date(vmExpiry * 1000);
    setState((prevState) => ({
      ...prevState,
      vmExpiry: vmDate,
      vm: vm && !isDateInPast(vmDate),
      hasHours: hours_remaining > 0.01 || expiring_hours_remaining.length > 0,
    }));
    setPrivatePcSelected(!((hours_remaining > 0.01 && !vm) || gameFromParams));
  };

  const checkInstanceState = async () => {
    try {
      const gamingRigStatusResp = await fetch(gamingRigStatusLink, {
        method: "GET",
        headers: {
          "Content-Type": "application/json;charset=UTF-8",
          Authorization: user.signInUserSession.idToken.jwtToken,
        },
      });
      if (!gamingRigStatusResp.ok) {
        throw new Error(`HTTP error! status: ${gamingRigStatusResp.status}`);
      }
      const gamingRigStatusJson = await gamingRigStatusResp.json();
      const gamingRigStatusStr = gamingRigStatusJson.state;

      setPaymentPlanState(gamingRigStatusJson);

      if (gamingRigStatusStr === "not_running") {
        setState((prevState) => ({
          ...prevState,
          gamingRigStatus: gamingRigStatusOptions.not_running,
          hoursRemaining: gamingRigStatusJson.hours_remaining,
          expiringHoursRemaining: gamingRigStatusJson.expiring_hours_remaining,
          hasShownRigStartNotification: false,
        }));
      } else if (gamingRigStatusStr === "booting") {
        setState((prevState) => ({
          ...prevState,
          gamingRigStatus: gamingRigStatusOptions.booting,
          hoursRemaining: gamingRigStatusJson.hours_remaining,
          expiringHoursRemaining: gamingRigStatusJson.expiring_hours_remaining,
          gameName: gamingRigStatusJson.game_name,
          region: gamingRigStatusJson.region,
        }));
        setPrivatePcSelected(gamingRigStatusJson.game_name === "VM");
        setTimeout(() => {
          checkInstanceState();
        }, 10000);
      } else if (
        gamingRigStatusStr === "running" ||
        gamingRigStatusStr === "starting"
      ) {
        !state.hasShownRigStartNotification && showNotification("Game ready.");
        setState((prevState) => ({
          ...prevState,
          gamingRigStatus: gamingRigStatusOptions.running,
          gamingRigIp: gamingRigStatusJson.public_ip,
          pinUrl: gamingRigStatusJson.pin_url,
          hoursRemaining: gamingRigStatusJson.hours_remaining,
          expiringHoursRemaining: gamingRigStatusJson.expiring_hours_remaining,
          gameName: gamingRigStatusJson.game_name,
          region: gamingRigStatusJson.region,
        }));
        setPrivatePcSelected(gamingRigStatusJson.game_name === "VM");
        setTimeout(() => {
          checkInstanceState();
        }, 120000);
      }
    } catch (err) {
      console.log("error in checkInstanceState", err);
    }
  };

  const handleGameChange = async (value) => {
    if (value === "VM" && state.closestRegion) {
      setState((prevState) => ({
        ...prevState,
        region: state.closestRegion,
        gameName: value,
        showGameSuggestion: value === "...request game",
      }));
    } else {
      localStorage.setItem("gameSelection", value);
      setState((prevState) => ({
        ...prevState,
        region: "ap-south-1",
        gameName: value,
        showGameSuggestion: value === "...request game",
      }));
    }
  };

  const handleRegionChange = (value) => {
    setState((prevState) => ({
      ...prevState,
      region: value,
    }));
    localStorage.setItem("serverSelection", value);
  };

  const formatHours = function (hoursToFormat) {
    if (hoursToFormat < 0) {
      return "0h 0m";
    }
    const hours = Math.floor(hoursToFormat);
    const minutes = Math.floor((hoursToFormat * 60) % 60);

    return `${hours}h ${minutes}m`;
  };

  const startVirtualGamingPC = (event) => {
    startGamingRig("VM");
  };

  const gameValue = function getGameValue(game) {
    return game.store === "Epic" ? `${game.name} ${game.store}` : game.name;
  };

  const { userEmail } = user;
  const allowGameplay =
    state.gamingRigStatus === gamingRigStatusOptions.client_loading ||
    state.hasHours;

  const addHoursButton = PURCHASES_CLOSED ? (
    <div className="pt-4">
      <Link to="/shutdown">
        <button className="btn btn-outline btn-accent w-full">
          {t("purchases_closed")}
        </button>
      </Link>
    </div>
  ) : (
    <div className="pt-4">
      <Link to="pricing" state={{ email: userEmail }}>
        <button className="btn btn-outline btn-accent w-full">
          {t("add_more_hours")}
        </button>
      </Link>
    </div>
  );

  const renderUpgradeButton = PURCHASES_CLOSED ? (
    <Link to="/shutdown" className="w-full">
      <Button variant="outline" asChild className="rounded-full w-full">
        <div>{t("purchases_closed")}</div>
      </Button>
    </Link>
  ) : (
    <>
      <Link to="/pricing" className="w-full">
        <Button variant="default" asChild className="rounded-full w-full">
          <div>
            <div>
              {privatePcSelected
                ? "Upgrade to Private Gaming PC"
                : "Buy Quick Play PC"}
            </div>
          </div>
        </Button>
      </Link>
    </>
  );

  const renderPrivatePcBenefits = (
    <div className="self-stretch flex-col justify-start items-start gap-4 flex">
      <div className="self-stretch justify-start items-start gap-3 inline-flex">
        <Check className="text-neutral-50 w-6 h-6 pl-[6.35px] pr-[5.65px] pt-[7.12px] pb-[6.38px] bg-green-600 rounded-full justify-center items-center flex overflow-hidden"></Check>
        <div className="grow shrink basis-0 flex-col justify-start items-start inline-flex">
          <div className="self-stretch text-[var(--muted-foreground)] text-base font-normal leading-normal">
            {t("cloud_local_saves")}
          </div>
        </div>
      </div>
      <div className="self-stretch justify-start items-start gap-3 inline-flex">
        <Check className="text-neutral-50 w-6 h-6 pl-[6.35px] pr-[5.65px] pt-[7.12px] pb-[6.38px] bg-green-600 rounded-full justify-center items-center flex overflow-hidden"></Check>
        <div className="grow shrink basis-0 flex-col justify-start items-start inline-flex">
          <div className="self-stretch text-[var(--muted-foreground)] text-base font-normal leading-normal">
            {t("install_your_games")}
          </div>
        </div>
      </div>
      <div className="self-stretch justify-start items-start gap-3 inline-flex">
        <Check className="text-neutral-50 w-6 h-6 pl-[6.35px] pr-[5.65px] pt-[7.12px] pb-[6.38px] bg-green-600 rounded-full justify-center items-center flex overflow-hidden"></Check>
        <div className="grow shrink basis-0 flex-col justify-start items-start inline-flex">
          <div className="self-stretch text-[var(--muted-foreground)] text-base font-normal leading-normal">
            {t("customizable_storage_space")}
          </div>
        </div>
      </div>
      <div className="self-stretch justify-start items-start gap-3 inline-flex">
        <Check className="text-neutral-50 w-6 h-6 pl-[6.35px] pr-[5.65px] pt-[7.12px] pb-[6.38px] bg-green-600 rounded-full justify-center items-center flex overflow-hidden"></Check>
        <div className="grow shrink basis-0 flex-col justify-start items-start inline-flex">
          <div className="self-stretch text-[var(--muted-foreground)] text-base font-normal leading-normal">
            {t("shareable_with_friends")}
          </div>
        </div>
      </div>
    </div>
  );

  const renderQuickPlayPcBenefits = (
    <div className="self-stretch flex-col justify-start items-start gap-4 flex">
      <div className="self-stretch justify-start items-start gap-3 inline-flex">
        <Check className="text-neutral-50 w-6 h-6 pl-[6.35px] pr-[5.65px] pt-[7.12px] pb-[6.38px] bg-green-600 rounded-full justify-center items-center flex overflow-hidden"></Check>
        <div className="grow shrink basis-0 flex-col justify-start items-start inline-flex">
          <div className="self-stretch text-[var(--muted-foreground)] text-base font-normal leading-normal">
            {t("pre_installed_and_shared")}
          </div>
        </div>
      </div>
      <div className="self-stretch justify-start items-start gap-3 inline-flex">
        <Check className="text-neutral-50 w-6 h-6 pl-[6.35px] pr-[5.65px] pt-[7.12px] pb-[6.38px] bg-green-600 rounded-full justify-center items-center flex overflow-hidden"></Check>
        <div className="grow shrink basis-0 flex-col justify-start items-start inline-flex">
          <div className="self-stretch text-[var(--muted-foreground)] text-base font-normal leading-normal">
            {t("cloud_saves_only")}
          </div>
        </div>
      </div>
    </div>
  );

  const renderBootingState = (
    <>
      <div className="self-stretch justify-start items-center gap-2 inline-flex">
        <div className="grow shrink basis-0 h-10 justify-start items-center gap-4 flex">
          <div className="text-right text-neutral-50 text-sm font-medium leading-[14px]">
            Moonlight IP
          </div>
          <div className="grow shrink basis-0 flex-col justify-start items-start gap-2 inline-flex">
            <div className="self-stretch h-10 px-3 py-2 bg-[var(--button-background)] rounded-xl border border-[var(--border-tertiary)] justify-start items-center gap-1 inline-flex overflow-hidden">
              <div className="grow shrink basis-0 text-neutral-50 text-sm font-normal leading-tight">
                Loading...
              </div>
            </div>
          </div>
        </div>
        <Button variant="secondary" disabled className="rounded-full w-fit">
          <div className="gap-2 inline-flex items-center">
            <Copy />
            <div>Copy</div>
          </div>
        </Button>
      </div>
      <div className="self-stretch justify-start items-center gap-2 inline-flex">
        <div className="grow shrink basis-0 h-10 justify-start items-center gap-4 flex">
          <div className="text-right text-neutral-50 text-sm font-medium leading-[14px]">
            PIN
          </div>
          <div className="grow shrink basis-0 flex-col justify-start items-start gap-2 inline-flex">
            <div className="self-stretch h-10 px-3 py-2 bg-[var(--button-background)] rounded-xl border border-[var(--border-tertiary)] justify-start items-center gap-1 inline-flex overflow-hidden">
              <div className="grow shrink basis-0 text-[var(--muted-foreground)] text-sm font-normal leading-tight">
                1234
              </div>
            </div>
          </div>
        </div>
        <div className="px-4 py-2 opacity-50 bg-fuchsia-700 rounded-full justify-center items-center gap-2 flex">
          <div className="text-neutral-50 text-sm font-semibold capitalize leading-tight">
            Set PIN
          </div>
        </div>
      </div>
      <div className="h-[0.5px] w-full bg-[var(--button-background)]"></div>
      <div className="self-stretch h-10 justify-start items-start gap-4 flex">
        <div className="self-stretch h-10 flex-col justify-start gap-2 flex w-full items-start">
          <div className="self-stretch justify-start items-center gap-1 inline-flex">
            <Loader className="text-neutral-50 animate-spin w-4 h-4 p-[1.33px] justify-center items-center flex overflow-hidden" />
            <div className="grow shrink basis-0 text-neutral-50 text-sm font-medium leading-[14px]">
              Booting...
            </div>
            {state.cancellingBoot ? (
              <div className="text-sm text-muted-foreground">Cancelling...</div>
            ) : (
              <Button
                variant="link"
                onClick={shutdownGamingRig}
                className="w-fit h-fit p-0 text-destructive"
              >
                Cancel
              </Button>
            )}
          </div>
          <div className="w-full h-2 bg-[var(--button-background)] rounded-full justify-start items-center inline-flex overflow-hidden">
            <div
              className="h-4 relative bg-cyan-500"
              style={{ width: `${state.progress}%` }}
            ></div>
          </div>
        </div>
      </div>
    </>
  );

  const renderPoweringOffButton = (
    <Button
      disabled
      variant="secondary"
      className="rounded-full w-full self-stretch justify-center items-center gap-2 flex flex-row cursor-pointer"
    >
      <div className="gap-2 inline-flex items-center">
        <Loader className="animate-spin" />
        <div>Powering Off...</div>
      </div>
    </Button>
  );

  const renderPowerOffButton = (
    <>
      <div className="mr-2 text-accent text-sm">
        Don't forget to adjust your settings in Moonlight for the best
        resolution! See{" "}
        <a
          className="underline cursor-pointer"
          onClick={() => setHowToOpen(!howToOpen)}
        >
          {t("connection_guide")}
        </a>{" "}
        for more.
      </div>
      <Button
        onClick={shutdownGamingRig}
        disabled={state.loadingRigIsStartingStopping}
        variant="secondary"
        asChild
        className="rounded-full w-full self-stretch justify-center items-center gap-2 flex flex-row cursor-pointer"
      >
        <div>
          <Power />
          <div>Power Off</div>
        </div>
      </Button>
    </>
  );

  const handleAppLaunch = (ip) => {
    setLaunchingApp(true);
    const appUrl = "gameaway://" + ip;
    const timeout = setTimeout(() => {
      setLaunchingApp(false);
      setDownloadingApp(true);
    }, 2500);

    window.location.href = appUrl;

    window.onblur = () => {
      clearTimeout(timeout);
      setLaunchingApp(false);
    };
  };

  const renderRunningState = (
    <>
      <div className="flex flex-col gap-4 w-full">
        <div className={cn(downloadUrl ? "flex" : "hidden")}>
          <DownloadAppDialog
            downloadLink={downloadUrl}
            open={downloadingApp}
            setOpen={() => setDownloadingApp(!downloadingApp)}
          />
          {launchingApp ? (
            <Button
              variant="disabled"
              className="rounded-full w-full inline-flex"
            >
              <div className="gap-2 inline-flex items-center">
                <Loader className="text-neutral-50 w-4 h-4 animate-spin" />
                <div>Downloading/Launching App...</div>
              </div>
            </Button>
          ) : (
            <Button
              variant="secondary"
              className="rounded-full w-full inline-flex"
              onClick={() => handleAppLaunch(state.gamingRigIp)}
            >
              <div className="gap-2 inline-flex items-center">
                <AppWindowMac className="text-neutral-50 w-4 h-4" />
                <div>(Beta) Launch in GameAway App</div>
              </div>
            </Button>
          )}
        </div>
        <div
          className={cn(downloadUrl ? "flex" : "hidden", "items-center gap-4")}
        >
          <Separator className="flex-1" />
          <span className="text-muted-foreground">or</span>
          <Separator className="flex-1" />
        </div>
        <div className="self-stretch justify-start items-center gap-2 inline-flex">
          <div className="grow shrink basis-0 h-10 justify-start items-center gap-4 flex">
            <div className="text-right text-neutral-50 text-sm font-medium leading-[14px]">
              Moonlight IP
            </div>
            <div className="grow shrink basis-0 flex-col justify-start items-start gap-2 inline-flex">
              <div className="self-stretch h-10 px-3 py-2 bg-[var(--button-background)] rounded-xl border border-[var(--border-tertiary)] justify-start items-center gap-1 inline-flex overflow-hidden">
                <div className="grow shrink basis-0 text-neutral-50 text-sm font-normal leading-tight">
                  {state.gamingRigIp}
                </div>
              </div>
            </div>
          </div>
          <Button
            variant="secondary"
            className="rounded-full w-fit"
            onClick={() => handleIpCopy(formattedIp)}
          >
            <div className="gap-2 inline-flex items-center">
              {!state.showCopiedTooltip && <Copy />}
              {state.showCopiedTooltip && <CopyCheck />}
              <div>Copy</div>
            </div>
          </Button>
        </div>
        <div className="self-stretch justify-start items-center gap-2 inline-flex">
          <div className="grow shrink basis-0 h-10 justify-start items-center gap-4 flex">
            <div className="text-right text-neutral-50 text-sm font-medium leading-[14px]">
              PIN
            </div>
            <Input
              type="number"
              placeholder="Enter PIN Here"
              onChangeCapture={(e) => handlePinChange(e.currentTarget.value)}
              className="bg-gray-900 border-[var(--border-tertiary)] text-neutral-50 rounded-xl py-0"
            />
          </div>
          <Button
            variant="default"
            asChild
            className="rounded-full w-fit cursor-pointer"
            onClick={() => setPin()}
          >
            <div>
              {state.pinSubmitted && (
                <Loader className="text-neutral-50 animate-spin w-4 h-4 p-[1.33px] justify-center items-center flex overflow-hidden" />
              )}
              {state.showPinTick && (
                <Check className="text-neutral-50 w-4 h-4 p-[1.33px] justify-center items-center flex overflow-hidden" />
              )}
              <div>Set PIN</div>
            </div>
          </Button>
        </div>
      </div>
      <div className="h-[0.5px] w-full bg-[var(--button-background)]"></div>
      {state.loadingRigIsStartingStopping
        ? renderPoweringOffButton
        : renderPowerOffButton}
    </>
  );

  const renderValidPrivatePcState = (
    <div className="self-stretch flex-col justify-start items-start gap-2 flex">
      <div className="self-stretch text-neutral-50 text-sm font-medium leading-[14px]">
        Server Location
      </div>
      <Select
        className="self-stretch h-10 px-3 py-2 bg-[var(--button-background)] rounded-xl border border-[var(--border-tertiary)] justify-between items-center inline-flex overflow-hidden w-full"
        value={state.region}
        onValueChange={handleRegionChange}
      >
        <SelectTrigger className="rounded-xl border border-[var(--border-tertiary)] bg-[var(--button-background)] grow shrink basis-0 text-neutral-50 text-sm font-normal leading-tight">
          <SelectValue placeholder="Select language" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="ap-south-1">🇮🇳 India</SelectItem>
          <SelectItem value="me-south-1">🇦🇪 Middle East</SelectItem>
          <SelectItem value="ap-east-1">🇻🇳 SE Asia</SelectItem>
          <SelectItem value="eu-south-1">🇹🇷 Turkey</SelectItem>
          <SelectItem value="sa-east-1">🇧🇷 S America</SelectItem>
          <SelectItem value="us-east-1">🇺🇸 US (East)</SelectItem>
          <SelectItem value="us-west-2">🇺🇸 US (West)</SelectItem>
        </SelectContent>
      </Select>
      <div className="self-stretch text-[var(--muted-foreground)] text-sm font-normal leading-tight">
        Pick a location that’s closest to you
      </div>
      {state.startGamePleaseWait && (
        <span className="mr-2 text-error text-sm">
          {t("please_wait_storing_data")}
        </span>
      )}
    </div>
  );

  const renderPowerOnButton = (
    <Button
      onClick={
        privatePcSelected ? startVirtualGamingPC : () => startGamingRig()
      }
      variant="default"
      asChild
      className="rounded-full w-full self-stretch justify-center items-center gap-2 flex flex-row cursor-pointer"
    >
      <div>
        <Power />
        <div>Power On</div>
      </div>
    </Button>
  );

  const renderValidQuickPlayPcState = (
    <div className="self-stretch flex-col justify-start items-start gap-2 flex">
      <div className="self-stretch text-neutral-50 text-sm font-medium leading-[14px]">
        Pick Your Game
      </div>
      <Select
        className="self-stretch h-10 px-3 py-2 bg-[var(--button-background)] rounded-xl border border-[var(--border-tertiary)] justify-between items-center inline-flex overflow-hidden w-full"
        value={state.gameName}
        onValueChange={handleGameChange}
      >
        <SelectTrigger className="rounded-xl border border-[var(--border-tertiary)] bg-[var(--button-background)] grow shrink basis-0 text-neutral-50 text-sm font-normal leading-tight">
          <SelectValue placeholder="Select game" />
        </SelectTrigger>
        <SelectContent>
          {[
            ...sharedVms.map((email) => (
              <SelectItem key={email} value={email}>
                {`${email}'s Virtual Gaming PC`}
              </SelectItem>
            )),
            ...gameList
              .sort((a, b) => a.name.localeCompare(b.name))
              .map((game) => (
                <SelectItem key={game.name} value={gameValue(game)}>
                  <div className="md:w-[660px] lg:w-[580px] w-full flex justify-between items-center gap-2.5">
                    <span>
                      {game.name}
                      {game.store === "Epic" ? (
                        <span> ({game.store})</span>
                      ) : (
                        ``
                      )}
                    </span>
                    <span className="flex items-end">
                      {game.free && (
                        <span className="px-2.5 py-0.5 bg-green-300 rounded-full border border-white/0 justify-center items-center gap-2.5">
                          <span className="text-green-950 text-xs font-semibold leading-none">
                            Free on {game.store}
                          </span>
                        </span>
                      )}
                    </span>
                  </div>
                </SelectItem>
              )),
          ]}
        </SelectContent>
      </Select>
      <div className="w-4 h-4 relative overflow-hidden"></div>
    </div>
  );

  const buyButtonOrPlanExpiryDate = addHoursButton;

  const expiredMessage = PURCHASES_CLOSED ? (
    <p>
      GameAway is shutting down on {SHUTDOWN_DATE_TEXT}. Purchases are closed.
    </p>
  ) : (
    <p>Continue playing by purchasing more hours</p>
  );

  const renderMainCard = function () {
    if (state.gamingRigStatus === gamingRigStatusOptions.booting) {
      return renderBootingState;
    } else if (state.gamingRigStatus === gamingRigStatusOptions.running) {
      return renderRunningState;
    } else if (
      state.gamingRigStatus === gamingRigStatusOptions.client_loading
    ) {
      return (
        <>
          <div className="space-y-2 w-full">
            <Skeleton className="h-4 w-28" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-4 w-60" />
          </div>
          <div className="h-[0.5px] w-full bg-[var(--button-background)]"></div>
          <Button disabled className="rounded-full w-full inline-flex">
            <div>
              <div>Loading...</div>
            </div>
          </Button>
        </>
      );
    } else {
      if (privatePcSelected) {
        if (state.vm) {
          return (
            <>
              {renderValidPrivatePcState}
              <div className="h-[0.5px] w-full bg-[var(--button-background)]"></div>
              {renderPowerOnButton}
            </>
          );
        } else {
          return (
            <>
              {renderPrivatePcBenefits}
              <div className="h-[0.5px] w-full bg-[var(--button-background)]"></div>
              {renderUpgradeButton}
            </>
          );
        }
      } else {
        if (state.hasHours) {
          return (
            <>
              {renderValidQuickPlayPcState}
              <div className="h-[0.5px] w-full bg-[var(--button-background)]"></div>
              {renderPowerOnButton}
            </>
          );
        } else {
          return (
            <>
              {renderQuickPlayPcBenefits}
              <div className="h-[0.5px] w-full bg-[var(--button-background)]"></div>
              {renderUpgradeButton}
            </>
          );
        }
      }
    }
  };

  let hoursRemainingStr = formatHours(state.hoursRemaining);

  const formattedIp = isIOS()
    ? `[::ffff:${state.gamingRigIp}]`
    : state.gamingRigIp;

  return (
    <div>
      <div className="pt-8 flex-col justify-start items-center gap-6 inline-flex w-full">
        <div className="self-stretch pb-6 flex-col justify-start items-start gap-5 flex">
          <div className="self-stretch justify-start items-start gap-4 lg:flex-row flex flex-col">
            <div className="grow shrink basis-0 justify-start items-start gap-5 flex flex-col lg:flex-row">
              <div className="w-10 h-10 relative">
                <div className="w-10 h-10 left-[11.86px] top-[-10.49px] absolute origin-top-left rotate-[15deg] bg-fuchsia-700 rounded-lg"></div>
                <div className="w-10 h-10 p-2.5 left-0 top-0 absolute bg-white/50 rounded-lg backdrop-blur-lg justify-center items-center inline-flex">
                  <Clock className="text-neutral-50 w-5 h-5 relative flex-col justify-start items-start flex overflow-hidden" />
                </div>
              </div>
              <div>
                <div className="group relative inline-block w-fit">
                  {/* Always visible: Total hours */}
                  <div className="flex-col justify-start items-start inline-flex w-fit">
                    <div className="self-stretch text-neutral-50 text-3xl font-bold leading-9 ">
                      {state.gamingRigStatus ===
                        gamingRigStatusOptions.client_loading
                        ? "Loading..."
                        : formatHours(
                          Math.max(parseFloat(state.hoursRemaining), 0) +
                          state.expiringHoursRemaining.reduce(
                            (sum, obj) =>
                              sum +
                              Math.max(parseFloat(obj.hoursRemaining), 0),
                            0
                          )
                        )}
                    </div>
                    <div className="self-stretch text-[var(--muted-foreground)] text-base font-normal leading-normal decoration-dotted underline cursor-pointer">
                      {t("remaining")}
                    </div>
                  </div>

                  {/* Hidden breakdown, visible on hover */}
                  {state.gamingRigStatus !==
                    gamingRigStatusOptions.client_loading && (
                      <div className="absolute top-full mt-2 left-0 z-10 hidden group-hover:flex flex-col gap-2 bg-background p-3 rounded-lg shadow-lg w-max border border-border">
                        {/* Non-expiring */}
                        <div className="flex flex-col">
                          <div className="text-neutral-50 text-base font-semibold">
                            {`${formatHours(state.hoursRemaining)}`}
                          </div>
                          <div className="text-[var(--muted-foreground)] text-sm">
                            does not expire
                          </div>
                        </div>

                        {/* Expiring chunks */}
                        {state.expiringHoursRemaining.map((hourObj, index) => (
                          <div key={index} className="flex flex-col">
                            <div className="text-neutral-50">+</div>
                            <div className="text-neutral-50 text-base font-semibold">
                              {`${formatHours(hourObj.hoursRemaining)}`}
                            </div>
                            <div className="text-[var(--muted-foreground)] text-sm">
                              {`until ${new Date(
                                hourObj.expiryDate * 1000
                              ).toDateString()}`}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                </div>
                {state.vm && (
                  <div className="flex-col justify-start items-start inline-flex w-fit">
                    <div className="self-stretch text-neutral-50 text-3xl font-bold leading-9">
                      {state.gamingRigStatus ===
                        gamingRigStatusOptions.client_loading
                        ? ""
                        : "+ Private Gaming PC"}
                    </div>
                    <div className="self-end text-[var(--muted-foreground)] text-base font-normal leading-normal">
                      <div className="flex flex-col items-center">
                        {`until ${state.vmExpiry.toDateString()}`}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="justify-start items-center gap-3 flex">
              <HowToStart howToOpen={howToOpen} setHowToOpen={setHowToOpen} />
              <AddPlaytimeDialog user={user} paymentGateway={paymentGateway} />
            </div>
          </div>
        </div>
      </div>
      <div className="py-8 justify-start items-start gap-12 flex flex-col lg:flex-row w-full">
        <div className="justify-start items-start gap-4 flex flex-row lg:flex-col w-full lg:w-1/3 mx-auto">
          <Button
            variant="ghost"
            className={cn(
              !privatePcSelected
                ? "bg-neutral-50 bg-opacity-5"
                : "bg-[var(--button-background)]",
              "w-full h-full px-4 pt-4 pb-5 justify-start rounded-2xl items-start gap-3 hover:bg-[var(--button-background)]"
            )}
            disabled={
              state.gamingRigStatus === gamingRigStatusOptions.booting ||
              state.gamingRigStatus === gamingRigStatusOptions.running
            }
            onClick={() => setPrivatePcSelected(true)}
          >
            <div
              className={cn(
                !privatePcSelected ? "bg-gray-800" : "bg-fuchsia-700",
                "w-8 h-8 p-2 rounded-full justify-center items-center hidden lg:flex overflow-hidden"
              )}
            >
              <PcCase className="w-4 h-4 relative flex-col justify-start items-start flex overflow-hidden text-neutral-50" />
            </div>
            <div className="grow shrink pt-0.5 flex-col justify-start items-start gap-0.5 inline-flex text-center lg:text-left">
              <div className="hidden lg:block self-stretch text-[var(--sidebar-foreground)] text-lg font-semibold leading-7">
                Private Gaming PC
              </div>
              <div className="block lg:hidden self-stretch text-[var(--sidebar-foreground)] text-lg font-semibold leading-7">
                Private PC
              </div>
              <div className="self-stretch text-[var(--muted-foreground)] text-sm font-normal leading-tight hidden lg:block">
                Play your own games
              </div>
            </div>
          </Button>
          <Button
            variant="ghost"
            className={cn(
              privatePcSelected
                ? "bg-neutral-50 bg-opacity-5"
                : "bg-[var(--button-background)]",
              "w-full h-full px-4 pt-4 pb-5 justify-start rounded-2xl items-start gap-3 text-left hover:bg-[var(--button-background)]"
            )}
            disabled={
              state.gamingRigStatus === gamingRigStatusOptions.booting ||
              state.gamingRigStatus === gamingRigStatusOptions.running
            }
            onClick={() => setPrivatePcSelected(false)}
          >
            <div
              className={cn(
                privatePcSelected ? "bg-gray-800" : "bg-fuchsia-700",
                "w-8 h-8 p-2 rounded-full justify-center items-center hidden lg:flex overflow-hidden"
              )}
            >
              <Gamepad className="w-4 h-4 relative flex-col justify-start items-start flex overflow-hidden text-neutral-50" />
            </div>
            <div className="grow shrink basis-0 pt-0.5 flex-col justify-start items-start gap-0.5 inline-flex text-center lg:text-left">
              <div className="self-stretch text-[var(--sidebar-foreground)] text-lg font-semibold leading-7">
                Quick Play PC
              </div>
              <div className="self-stretch text-[var(--muted-foreground)] text-sm font-normal leading-tight hidden lg:block">
                Play our pre-installed games
              </div>
              <div className="self-stretch text-[var(--muted-foreground)] text-sm font-normal leading-tight hidden lg:block">
                or on a friend's shared PC
              </div>
            </div>
          </Button>
        </div>
        <div className="grow shrink basis-0 flex-col justify-start items-start gap-8 inline-flex w-full lg:w-2/3">
          <div className="self-stretch p-5 bg-[var(--ring-offset)] rounded-3xl shadow-sm border border-[var(--button-background)] flex-col justify-start items-start gap-6 flex">
            <div className="flex flex-col lg:flex-row gap-4 w-full justify-between">
              {privatePcSelected ? (
                <div className="self-stretch justify-start items-start gap-4 inline-flex">
                  <div className="w-12 h-12 relative rounded-3xl">
                    <div
                      className={cn(
                        state.gamingRigStatus ===
                          gamingRigStatusOptions.booting ||
                          state.gamingRigStatus ===
                          gamingRigStatusOptions.running
                          ? "from-fuchsia-900"
                          : "from-gray-700",
                        "w-12 h-12 items-center justify-center absolute bg-gradient-to-b to-black rounded-full"
                      )}
                    >
                      <div
                        className={cn(
                          state.gamingRigStatus ===
                            gamingRigStatusOptions.booting ||
                            state.gamingRigStatus ===
                            gamingRigStatusOptions.running
                            ? "from-fuchsia-950"
                            : "from-gray-800",
                          "bg-gradient-to-b to-black w-[46px] h-[46px] left-[1px] top-[1px] absolute rounded-full border-gradient-to-b border-transparent"
                        )}
                      ></div>
                    </div>
                    <div
                      className={cn(
                        state.gamingRigStatus ===
                          gamingRigStatusOptions.booting ||
                          state.gamingRigStatus ===
                          gamingRigStatusOptions.running
                          ? "bg-fuchsia-700"
                          : "bg-gray-700",
                        "w-8 h-8 left-[8px] top-[8px] absolute rounded-[99px] flex-col justify-center items-center inline-flex"
                      )}
                    >
                      <PcCase className="w-5 h-5 text-neutral-50"></PcCase>
                    </div>
                  </div>
                  <div className="grow shrink basis-0 flex-col justify-start items-start gap-0.5 inline-flex">
                    <div className="self-stretch text-neutral-50 text-xl font-semibold leading-7">
                      Private Gaming PC
                    </div>
                    <div className="self-stretch text-[var(--muted-foreground)] text-sm font-normal leading-tight">
                      {t("play_your_own_games")}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="self-stretch justify-start items-start gap-4 inline-flex">
                  <div className="w-12 h-12 relative rounded-3xl">
                    <div
                      className={cn(
                        state.gamingRigStatus ===
                          gamingRigStatusOptions.booting ||
                          state.gamingRigStatus ===
                          gamingRigStatusOptions.running
                          ? "from-fuchsia-900"
                          : "from-gray-700",
                        "w-12 h-12 items-center justify-center absolute bg-gradient-to-b to-black rounded-full"
                      )}
                    >
                      <div
                        className={cn(
                          state.gamingRigStatus ===
                            gamingRigStatusOptions.booting ||
                            state.gamingRigStatus ===
                            gamingRigStatusOptions.running
                            ? "from-fuchsia-950"
                            : "from-gray-800",
                          "bg-gradient-to-b to-black w-[46px] h-[46px] left-[1px] top-[1px] absolute rounded-full border-gradient-to-b border-transparent"
                        )}
                      ></div>
                    </div>
                    <div
                      className={cn(
                        state.gamingRigStatus ===
                          gamingRigStatusOptions.booting ||
                          state.gamingRigStatus ===
                          gamingRigStatusOptions.running
                          ? "bg-fuchsia-700"
                          : "bg-gray-700",
                        "w-8 h-8 left-[8px] top-[8px] absolute rounded-[99px] flex-col justify-center items-center inline-flex"
                      )}
                    >
                      <Gamepad className="w-5 h-5 text-neutral-50"></Gamepad>
                    </div>
                  </div>
                  <div className="grow shrink basis-0 flex-col justify-start items-start gap-0.5 inline-flex">
                    <div className="self-stretch text-neutral-50 text-xl font-semibold leading-7">
                      Quick Play PC
                    </div>
                    <div className="self-stretch text-[var(--muted-foreground)] text-sm font-normal leading-tight">
                      {t("choose_from_preinstalled")}
                    </div>
                  </div>
                </div>
              )}
              <Button
                variant="link"
                asChild
                className="rounded-full w-fit cursor-pointer text-accent"
                onClick={() => setHowToOpen(!howToOpen)}
              >
                <div>
                  <ChevronsLeftRightEllipsis />
                  <div>{t("connection_guide")}</div>
                </div>
              </Button>
            </div>
            <div className="h-[0.5px] w-full bg-[var(--button-background)]"></div>
            {renderMainCard()}
            {/*{state.vm ? (
              state.gamingRigStatus === gamingRigStatusOptions.booting ? (
                renderBootingState
              ) : state.gamingRigStatus === gamingRigStatusOptions.running ? (
                renderRunningState
              ) : (
                <>
                  {privatePcSelected
                    ? renderValidPrivatePcState
                    : renderValidQuickPlayPcState}
                  <div className="h-[0.5px] w-full bg-[var(--button-background)]"></div>
                  {allowGameplay ? renderPowerOnButton : renderUpgradeButton}
                </>
              )
            ) : (
              <>
                {" "}
                {state.gamingRigStatus ===
                  gamingRigStatusOptions.client_loading ? (
                  <>
                    <div className="space-y-2 w-full">
                      <Skeleton className="h-4 w-28" />
                      <Skeleton className="h-10 w-full" />
                      <Skeleton className="h-4 w-60" />
                    </div>
                  </>
                ) : privatePcSelected ? (
                  renderPrivatePcBenefits
                ) : (
                  renderQuickPlayPcBenefits
                )}
                <div className="h-[0.5px] w-full bg-[var(--button-background)]"></div>
                {state.gamingRigStatus ===
                  gamingRigStatusOptions.client_loading ? (
                  <Button disabled className="rounded-full w-full inline-flex">
                    <div>
                      <div>Loading...</div>
                    </div>
                  </Button>
                ) : (
                  renderUpgradeButton
                )}
              </>
            )}*/}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
